import { useSuspenseQuery } from '~/services'
import { useUpdateBot } from '../../../hooks'
import { showSuccessToast, showErrorToast } from '@bpinternal/ui-kit-next'
import { useState } from 'react'
import { getStudioClient } from '~/stores'

const INSIGHTS_PLUGIN_NAME = 'conversation-insights'

export const useInsightsPlugin = (workspaceId: string, botId: string) => {
  const { mutateAsync: updateBot } = useUpdateBot()
  const { data: bot } = useSuspenseQuery('workspaces_/$workspaceId_/bots_/$botId_', { workspaceId, botId })
  const { data: plugin } = useSuspenseQuery('plugins_/$pluginName_', { pluginName: INSIGHTS_PLUGIN_NAME })

  const insightsPlugin = bot.plugins[INSIGHTS_PLUGIN_NAME]

  const isPublishRequired = (() => {
    if (!insightsPlugin?.createdAt || !bot.deployedAt) return true
    return new Date(bot.deployedAt) < new Date(insightsPlugin.createdAt)
  })()

  const isPluginUpToDate = insightsPlugin?.id === plugin.id

  const studioClient = getStudioClient({ workspaceId: workspaceId, botId: botId })
  const [isBotUpdating, setIsBotUpdating] = useState(false)

  const [isAiEnabled, setIsAiEnabled] = useState(insightsPlugin?.configuration.aiEnabled || false)
  const [isPublishing, setIsPublishing] = useState(false)

  const defaultPluginInstall = async () => {
    setIsBotUpdating(true)
    await updateBot({
      id: botId,
      workspaceId: workspaceId,
      plugins: {
        [`${INSIGHTS_PLUGIN_NAME}`]: {
          id: plugin.id,
          enabled: true,
          configuration: { aiEnabled: false },
        },
      },
    })
    setIsBotUpdating(false)
  }

  const autoUpdatePlugin = async () => {
    if (isPluginUpToDate) return

    setIsBotUpdating(true)
    await updateBot({
      id: botId,
      workspaceId: workspaceId,
      plugins: {
        [`${INSIGHTS_PLUGIN_NAME}`]: {
          id: plugin.id,
        },
      },
    })
    setIsBotUpdating(false)
  }

  const enableAiInsights = async (options: { enabled: boolean; showToast: boolean }) => {
    setIsBotUpdating(true)
    await updateBot({
      id: botId,
      workspaceId: workspaceId,
      plugins: {
        [`${INSIGHTS_PLUGIN_NAME}`]: {
          id: plugin.id,
          enabled: true,
          configuration: { aiEnabled: options.enabled },
        },
      },
      onSuccess: async () => {
        await publishBot(options.enabled)
        if (options.showToast) {
          showSuccessToast(`AI has been successfully ${options.enabled ? 'enabled' : 'disabled'}`)
        }
      },
      onError: () => {
        showErrorToast(`An error occurred while ${options.enabled ? 'enabling' : 'disabling'} AI`)
      },
    })
    setIsBotUpdating(false)
  }

  const publishBot = async (enabled: boolean) => {
    setIsPublishing(true)
    try {
      await studioClient.post('/publish')
      setIsAiEnabled(enabled)
    } catch (error) {
      showErrorToast(`An error occurred while enabling/disabling AI Insights`)
    } finally {
      setIsPublishing(false)
    }
  }

  return {
    isAiEnabled,
    isPublishing,
    isBotUpdating,
    enableAiInsights,
    insightsPlugin,
    isPublishRequired,
    defaultPluginInstall,
    autoUpdatePlugin,
  }
}
